import axios from "axios";
import authHeader from "./authHeader";
import { userApi, baseUrl, host, tenant, adminApi } from "./endpoints";
import { CreateFuelOrderArgs, ExportArgs } from "../store/user-reducers/user-account-reducer/types";
import { CreatePayment, UpdatePaymentNameDescriptionArgs } from "../store/user-reducers/payment-reducer/types";
import { AccountArgs, ContactUsArgs, CreateAutoPayArgs, CreatePaymentArgs, MakeExistingPaymentArgs } from "MyAccountPlus-UIComponents";

export const getUserProfileService = async (email?: string) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.user + userApi.profile + `${email ? "/" + encodeURIComponent(email) : ""}`, {
        headers: authHeader()
    });
    return res;
};

export const updateProfileService = async (data: Object, email?: string) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.user + userApi.profile + `${email ? "/" + encodeURIComponent(email) : ""}`, data,
        {
            headers: authHeader()
        });
    return res;
};

export const getUserDocumentService = async (args: AccountArgs) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + "/" + args.account_id + userApi.documents + `?page=${args.page}&&size=${args.size}${args.sort ? "&&sort=" + args.sort + "," + args.order : ""}`, {
        headers: authHeader()
    });
    return res;
};

export const getUserTransactionService = async (args: AccountArgs) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + "/" + args.account_id + userApi.transactions + `?page=${args.page}&&size=${args.size}${args.sort ? "&&sort=" + args.sort + "," + args.order : ""}`, {
        headers: authHeader()
    });
    return res;
};

export const getUserDeliveryService = async (args: AccountArgs) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + "/" + args.account_id + userApi.deliveries + `?page=${args.page}&&size=${args.size}${args.sort ? "&&sort=" + args.sort + "," + args.order : ""}`, {
        headers: authHeader()
    });
    return res;
};

export const contactUsService = async (data: ContactUsArgs) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.inquiry,
        data, {
        headers: authHeader()
    });
    return res;
};

export const getUserAccountService = async () => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + userApi.userAccount, {
        headers: authHeader()
    });
    return res;
};

export const updateCurrentUserPasswordService = async (data: { new_password: string }, userKey: string) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.user + "s" + userApi.password,
        data,
        {
            headers: { ...authHeader(), "user-key": userKey },
        });
    return res;
};

export const downloadDocumentPDFService = async (document: string, account_id: string) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + "/" + account_id + userApi.documents + "/" + document, {
        headers: authHeader(),
        responseType: 'blob'
    });
    return res;
};

export const openDocumentPDFService = async (document: string) => {
    const res = await axios.get(document, {
        headers: authHeader(),
        responseType: 'blob'
    });
    return res;
};

export const paymentSourceService = async (account_id: string) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.payment_sources + userApi.userAccount + "/" + account_id, {
        headers: authHeader(),
    });
    return res;
};

export const autoPaymentService = async () => {
    const res = await axios.get(host + baseUrl + tenant + userApi.payment_sources + userApi.autopay + userApi.userAccount, {
        headers: authHeader(),
    });
    return res;
};

export const paymentSourceAlternateService = async (user_id: string) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.payment_sources + "/" + user_id, {
        headers: authHeader(),
    });
    return res;
};

export const addPaymentSourceService = async (data: CreatePayment) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.payment_sources, data, {
        headers: authHeader(),
    });
    return res;
};

export const updatePaymentNameDescriptionService = async (data: UpdatePaymentNameDescriptionArgs, payment_id: string) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.payment_sources + "/" + data.user_id + "/" + payment_id, data, {
        headers: authHeader(),
    });
    return res;
};

export const deletePaymentSourceService = async ({ user_id, payment_id }: { user_id: string, payment_id: string }) => {
    const res = await axios.delete(host + baseUrl + tenant + userApi.payment_sources + "/" + user_id + "/" + payment_id, {
        headers: authHeader(),
    });
    return res;
};

export const addMakePaymentService = async (data: CreatePaymentArgs | MakeExistingPaymentArgs) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.payment + userApi.make_payment, data, {
        headers: authHeader(),
    });
    return res;
};

export const addAutoPaymentService = async (data: CreateAutoPayArgs) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.payment_sources + userApi.autopay, data, {
        headers: authHeader(),
    });
    return res;
};

export const deleteAutoPaymentService = async ({ user_id, payment_id }: { user_id: string, payment_id: string }) => {
    const res = await axios.delete(host + baseUrl + tenant + userApi.payment_sources + userApi.autopay + "/" + user_id + "/" + payment_id, {
        headers: authHeader(),
    });
    return res;
};

export const makeDefaultPaymentService = async ({ user_id, payment_id }: { user_id: string, payment_id: string }) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.payment_sources + "/" + user_id + "/" + payment_id + userApi.makeDefault, {}, {
        headers: authHeader(),
    });
    return res;
};

export const createFuelOrderService = async (data: CreateFuelOrderArgs) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.orders + userApi.createFuelOrder, data, {
        headers: authHeader(),
    });
    return res;
};

export const calculateFuelOrderPriceService = async (data: CreateFuelOrderArgs) => {
    const res = await axios.post(host + baseUrl + tenant + userApi.orders + userApi.fuelOrderPrice, data, {
        headers: authHeader(),
    });
    return res;
};

export const getNotificationConfigForUserService = async () => {
    const res = await axios.get(host + baseUrl + tenant + adminApi.notificationsConfig + userApi.userAccount, {
        headers: authHeader()
    });
    return res;
};

export const exportUserDeliveryService = async (args: ExportArgs) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + "/" + args.account_id + userApi.deliveries + `/${args.format}`, {
        headers: authHeader(),
        responseType: 'blob'
    });
    return res.data;
};

export const exportUserTransactionService = async (args: ExportArgs) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.accounts + "/" + args.account_id + userApi.transactions + `/${args.format}`, {
        headers: authHeader(),
        responseType: 'blob'
    });
    return res.data;
};

export const getAddressSuggestionsService = async (query: string) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.address + "/suggestions", {
        params: { input: query },
        headers: authHeader()
    });
    return res;
};

export const getAddressDetailsService = async (placeId: string) => {
    const res = await axios.get(host + baseUrl + tenant + userApi.address + "/details", {
        params: { placeId },
        headers: authHeader()
    });
    return res;
};